import { useState, useEffect, useCallback } from 'react';
import { auditLogAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Search, Shield, Clock, User, FileText, Activity } from 'lucide-react';
import { cn } from '../lib/utils';

const ACTION_COLORS = {
  login: 'bg-blue-100 text-blue-700',
  logout: 'bg-slate-100 text-slate-600',
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-amber-100 text-amber-700',
  delete: 'bg-red-100 text-red-700',
  view: 'bg-purple-100 text-purple-700',
};

const getActionColor = (action) => {
  const lower = action?.toLowerCase() || '';
  for (const [key, cls] of Object.entries(ACTION_COLORS)) {
    if (lower.includes(key)) return cls;
  }
  return 'bg-slate-100 text-slate-600';
};

const AuditLogPage = () => {
  const { hospital } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, skip: page * limit };
      if (hospital?.id) params.hospital_id = hospital.id;
      if (search) params.action = search;
      const [logsRes, summaryRes] = await Promise.all([
        auditLogAPI.list(params),
        auditLogAPI.summary(hospital?.id ? { hospital_id: hospital.id } : {}),
      ]);
      setLogs(logsRes.data?.logs || []);
      setTotal(logsRes.data?.total || 0);
      setSummary(summaryRes.data);
    } catch { toast.error('Failed to load audit logs'); }
    setLoading(false);
  }, [hospital?.id, page, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="audit-log-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Audit Logs</h1>
        <p className="text-sm text-slate-500 mt-0.5">Track all system activities and user actions</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center"><Shield size={18} className="text-blue-600" /></div>
          <div><p className="text-[10px] text-slate-500 uppercase">Total Logs</p><p className="text-lg font-bold">{summary?.total || 0}</p></div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center"><Clock size={18} className="text-emerald-600" /></div>
          <div><p className="text-[10px] text-slate-500 uppercase">Last 24h</p><p className="text-lg font-bold">{summary?.recent_24h || 0}</p></div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center"><Activity size={18} className="text-purple-600" /></div>
          <div><p className="text-[10px] text-slate-500 uppercase">Action Types</p><p className="text-lg font-bold">{summary?.action_types?.length || 0}</p></div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center"><FileText size={18} className="text-amber-600" /></div>
          <div><p className="text-[10px] text-slate-500 uppercase">Showing</p><p className="text-lg font-bold">{logs.length} of {total}</p></div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Filter by action (login, create, update, delete...)" value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            className="pl-10 h-10" data-testid="search-audit" />
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading audit logs...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Shield size={40} className="mx-auto mb-3 opacity-30" />
            <p>No audit logs found</p>
            <p className="text-xs mt-1">System activities will appear here as they occur</p>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map(log => (
              <div key={log.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-50 hover:bg-slate-50/50 transition-all" data-testid={`audit-${log.id}`}>
                <div className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold uppercase min-w-[60px] text-center', getActionColor(log.action))}>
                  {log.action}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700">
                    <span className="font-semibold">{log.user_name || log.user_email || 'System'}</span>
                    {(log.resource_type || log.entity_type) && <span className="text-slate-400"> &middot; {log.resource_type || log.entity_type}</span>}
                  </p>
                  {log.details && <p className="text-xs text-slate-400 truncate">{typeof log.details === 'string' ? log.details : JSON.stringify(log.details)}</p>}
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-[10px] text-slate-400 font-mono">{new Date(log.timestamp).toLocaleString()}</p>
                  {log.ip_address && <p className="text-[9px] text-slate-300">{log.ip_address}</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-center gap-3 mt-4 pt-3 border-t border-slate-100">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 disabled:opacity-30 hover:bg-slate-50">Previous</button>
            <span className="text-xs text-slate-500">Page {page + 1} of {Math.ceil(total / limit)}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 disabled:opacity-30 hover:bg-slate-50">Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogPage;
